import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Flame, Calendar, TrendingUp, Target, Settings, Coffee, Scale } from "lucide-react";
import RankCard from "../components/RankCard";
import StrengthChart from "../components/StrengthChart";
import ImbalanceCard from "../components/ImbalanceCard";
import CutReminder from "../components/CutReminder";
import ProgressNotice from "../components/ProgressNotice";

export default function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [program, setProgram] = useState(null);
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        (async () => {
            const tzOffset = -new Date().getTimezoneOffset(); // signed minutes east of UTC
            const [s, p, w] = await Promise.all([
                api.get("/progress/stats").then(r => r.data),
                api.get(`/programs/${user.split || "upper_lower"}`, { params: { tz_offset_minutes: tzOffset } }).then(r => r.data),
                api.get("/workouts?limit=5").then(r => r.data),
            ]);
            setStats(s);
            setProgram(p);
            setRecent(w);
        })();
    }, [user.split]);

    const trialDaysLeft = user.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000)) : 0;
    // Backend tells us whether the user is currently in a rest window (extends
    // automatically across FB's end-of-week double-off). `next_day_index` is
    // the next workout day in the cycle either way.
    const isRest = !!program?.rest_day;
    const nextDay = program?.days?.[program?.next_day_index ?? 0];

    return (
        <div className="space-y-8" data-testid="dashboard">
            <CutReminder />
            <ProgressNotice />
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="label-eyebrow text-accent-red">Welcome back</div>
                    <h1 className="font-display text-4xl font-black uppercase sm:text-5xl">{user.name}</h1>
                </div>
                {user.subscription_status === "trial" && !user.admin_override_premium && (
                    <Link to="/pricing" data-testid="trial-banner" className="border border-accent-red bg-accent-red/10 px-4 py-2 text-xs font-bold uppercase tracking-wider text-accent-red hover:bg-accent-red/20">
                        {trialDaysLeft} days left in trial → Upgrade
                    </Link>
                )}
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Stat icon={<Flame />} label="Total workouts" value={stats?.total_workouts ?? "—"} />
                <Stat icon={<Calendar />} label="Last 30 days" value={stats?.workouts_last_30 ?? "—"} />
                <Stat icon={<Target />} label="Split" value={(user.split || "—").replace("_", "/").toUpperCase()} />
                <Stat icon={<TrendingUp />} label="Mode" value={(user.goal || "—").toUpperCase()} />
            </div>

            {/* Strength progression + rank — primary motivational block */}
            <div className="grid gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    <StrengthChart />
                </div>
                <div>
                    <RankCard />
                </div>
            </div>

            {/* Side Imbalance Watch — hidden when user has no unilateral data yet */}
            <ImbalanceCard />

            <div className="grid gap-6 lg:grid-cols-3">
                <div className="tactical-card p-6 lg:col-span-2" data-testid="next-session-card">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="label-eyebrow text-accent-red">{isRest ? "Rest day" : "Next session"}</div>
                                {typeof program?.workouts_this_week === "number" && (
                                    <span data-testid="weekly-counter" className="rounded-sm border border-border bg-secondary/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-foreground/80">
                                        {program.workouts_this_week} this week
                                    </span>
                                )}
                            </div>
                            <h2 className="mt-1 font-display text-3xl font-black uppercase">
                                {isRest ? "Recover" : (nextDay?.name || "Rest Day")}
                            </h2>
                            <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line">
                                {isRest
                                    ? "Your schedule says today is recovery. Eat, sleep, walk. The next training day comes around when the program says it does — not when you feel like it."
                                    : program?.schedule}
                            </p>
                            {isRest && nextDay && (
                                <p className="mt-3 text-xs uppercase tracking-wider text-foreground/80" data-testid="rest-next-up">
                                    <span className="text-muted-foreground">Coming up: </span>
                                    <span className="font-bold text-accent-red">{nextDay.name}</span>
                                    {program?.next_day_date_label && (
                                        <span className="text-muted-foreground"> — {program.next_day_date_label}</span>
                                    )}
                                </p>
                            )}
                            {!isRest && nextDay?.muscles_today?.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="muscles-today">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Training today:</span>
                                    {nextDay.muscles_today.map((m) => (
                                        <span key={m} className="rounded-sm border border-accent-red/40 bg-accent-red/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent-red">
                                            {m}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        {!isRest && nextDay && (
                            <Link to="/workouts" data-testid="start-workout" className="rounded-sm bg-accent-red px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)]">Start</Link>
                        )}
                    </div>
                    {isRest && (
                        <div className="mt-6 flex items-center gap-3 rounded-sm border border-border bg-secondary/30 p-4">
                            <Coffee className="h-6 w-6 text-accent-red" />
                            <p className="text-sm text-muted-foreground">Stretch, hydrate, walk 10k steps. No PRs are set on overtrained muscles.</p>
                        </div>
                    )}
                    {!isRest && nextDay && (
                        <div className="mt-6 space-y-3">
                            <p className="text-xs text-muted-foreground">
                                <span className="font-bold uppercase tracking-wider text-foreground/80">Slot preview.</span>
                                {" "}Hit <span className="text-accent-red">Start</span> to pick exercises and reorder slots for this session (custom order resets next workout).
                            </p>
                            {nextDay.slots.map((s, i) => (
                                <div key={`${s.label}-${i}`} className="flex items-center justify-between gap-3 rounded-sm border border-border bg-card p-3" data-testid={`next-slot-${i}`}>
                                    <div className="min-w-0">
                                        <div className="label-eyebrow text-muted-foreground">Slot {i + 1}</div>
                                        <div className="truncate text-sm font-bold">{s.label}</div>
                                    </div>
                                    {s.unilateral && (
                                        <span className="flex items-center gap-1 rounded-sm border border-accent-red/50 px-1.5 py-0.5 text-[10px] tracking-wider text-accent-red">
                                            <Scale className="h-2.5 w-2.5" /> UNI
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="space-y-4">
                    <div className="tactical-card p-6" data-testid="dash-quick-settings">
                        <div className="flex items-center justify-between">
                            <div className="label-eyebrow text-accent-red">Your plan</div>
                            <Link to="/account" data-testid="dash-edit-account" className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-accent-red hover:underline">
                                <Settings className="h-3 w-3" /> Edit
                            </Link>
                        </div>
                        <dl className="mt-4 space-y-3 text-sm">
                            <div className="flex items-center justify-between gap-2">
                                <dt className="label-eyebrow text-muted-foreground">Split</dt>
                                <dd className="font-bold">{(user.split || "—").replace("_", " / ").toUpperCase()}</dd>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <dt className="label-eyebrow text-muted-foreground">Mode</dt>
                                <dd className="font-bold">{(user.goal || "—").toUpperCase()}</dd>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <dt className="label-eyebrow text-muted-foreground">Experience</dt>
                                <dd className="font-bold">{(user.experience || "—").toUpperCase()}</dd>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <dt className="label-eyebrow text-muted-foreground">Step goal</dt>
                                <dd className="font-bold">{(user.step_goal || 0).toLocaleString()}</dd>
                            </div>
                        </dl>
                        <Link to="/account" data-testid="dash-change-split" className="mt-4 block rounded-sm border border-accent-red px-3 py-2 text-center text-xs font-bold uppercase tracking-wider text-accent-red hover:bg-accent-red hover:text-white">
                            Change split / mode
                        </Link>
                    </div>
                    <div className="tactical-card p-6">
                        <div className="label-eyebrow text-accent-red">Recent sessions</div>
                        <div className="mt-4 space-y-3">
                            {recent.length === 0 && <p className="text-sm text-muted-foreground">No sessions yet. Start your first one.</p>}
                            {recent.map(r => (
                                <Link key={r.log_id} to={`/session/${r.log_id}`} data-testid={`recent-${r.log_id}`} className="block border border-border p-3 hover:bg-secondary">
                                    <div className="text-sm font-bold">{r.day_name || "Session"}</div>
                                    <div className="text-xs text-muted-foreground">{new Date(r.started_at).toLocaleDateString()} · {r.finished_at ? "Completed" : "In progress"}</div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Stat({ icon, label, value }) {
    return (
        <div className="tactical-card p-5">
            <div className="text-accent-red">{icon}</div>
            <div className="mt-3 label-eyebrow">{label}</div>
            <div className="mt-1 font-display text-3xl font-black">{value}</div>
        </div>
    );
}
